import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const values = [
  {
    icon: "ri-heart-line",
    title: "Passion for Quality",
    desc: "We are passionate about delivering the highest quality windows and doors. Every product we install carries our name, and we never compromise on standards.",
  },
  {
    icon: "ri-handshake-line",
    title: "Honesty & Integrity",
    desc: "Straightforward advice, transparent pricing, and honest communication — always. We build lasting relationships based on trust.",
  },
  {
    icon: "ri-focus-3-line",
    title: "Attention to Detail",
    desc: "From precise measurement surveys to meticulous installation, every stage of our process is carried out with care and precision.",
  },
  {
    icon: "ri-user-heart-line",
    title: "Customer First",
    desc: "Your satisfaction is our priority. We listen, we advise, and we deliver exactly what you need — on time, on budget, and with a smile.",
  },
];

const team = [
  {
    name: "Michael Ensure",
    role: "Managing Director",
    bio: "With over 15 years in the glazing industry, Michael founded Ensure Windows with a mission to bring premium quality and honest service to homeowners across Surrey.",
    image: "https://public.readdy.ai/ai/img_res/7d8ab7e2504ca29a2082a7a4654ca6f8.jpg",
  },
  {
    name: "James Fletcher",
    role: "Head of Installations",
    bio: "James leads our installation teams with 12 years of hands-on experience. His commitment to flawless workmanship ensures every project exceeds expectations.",
    image: "https://readdy.ai/api/search-image?query=professional%20tradesman%20construction%20manager%20portrait%20smart%2040s%20confident%20white%20background&width=400&height=400&seq=team2&orientation=squarish",
  },
  {
    name: "Rachel Brown",
    role: "Customer Experience Manager",
    bio: "Rachel ensures every customer has a seamless, stress-free experience from first contact to aftercare. She is the heart of our customer care.",
    image: "https://readdy.ai/api/search-image?query=professional%20businesswoman%20portrait%20friendly%2035s%20smart%20casual%20white%20background%20uk%20corporate%20headshot&width=400&height=400&seq=team3&orientation=squarish",
  },
];

const certifications = [
  { icon: "ri-shield-check-line", name: "FENSA Registered" },
  { icon: "ri-verified-badge-line", name: "Which? Trusted Trader" },
  { icon: "ri-building-line", name: "Trading Standards Approved" },
  { icon: "ri-recycle-line", name: "Eco-Friendly Materials" },
];

export default function AboutPage() {
  return (
    <main>
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20uk%20home%20exterior%20aluminium%20windows%20doors%20elegant%20residential%20architecture%20garden%20premium%20quality%20bright%20daylight&width=1920&height=600&seq=about-hero&orientation=landscape"
            alt="About Ensure Windows"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-zinc-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Our Story
          </span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mt-4 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            About Ensure Windows
          </h1>
          <p className="text-stone-300 text-lg max-w-xl mx-auto" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Premium windows and doors, expertly installed by a team that genuinely cares.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Who We Are
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold text-zinc-900 mt-3 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Your Local Window &amp; Door Specialists
              </h2>
              <div className="space-y-4 text-zinc-500 text-base leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
                <p>
                  Ensure Windows was founded right here in Caterham, Surrey, with a simple but powerful vision — to give local homeowners access to premium-quality windows and doors, installed by specialists who truly care about the finished result.
                </p>
                <p>
                  Over the past 12 years, we have grown from a small local team into one of Surrey&apos;s most trusted glazing companies, completing over 500 installations across Caterham, Redhill, Oxted, Coulsdon, and beyond.
                </p>
                <p>
                  We are proud to be a family-run business. That means you deal directly with the people who do the work — no middlemen, no call centres, and no broken promises. We measure, supply, and install everything ourselves, so quality is never compromised.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/contact" className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  Get in Touch
                </Link>
                <Link to="/gallery" className="border-2 border-zinc-800 text-zinc-800 hover:bg-zinc-800 hover:text-white font-semibold px-6 py-3 rounded-md transition-all whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  View Our Work
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[480px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=windows%20doors%20showroom%20interior%20display%20elegant%20uk%20professional%20modern%20aluminium%20frames%20bifold%20demonstration%20bright%20clean&width=700&height=480&seq=about1&orientation=landscape"
                  alt="Ensure Windows showroom"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white rounded-lg p-6 w-44 text-center">
                <p className="text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>500+</p>
                <p className="text-xs mt-1" style={{ fontFamily: "'Raleway', sans-serif" }}>Happy Customers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Our Values
            </span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              The Principles That Guide Everything We Do
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-lg p-7 border border-stone-100">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-lg mb-5">
                  <i className={`${v.icon} text-amber-600 text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-zinc-800 mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{v.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Meet the Team
            </span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              The People Behind Ensure Windows
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member) => (
              <div key={member.name} className="text-center">
                <div className="w-36 h-36 mx-auto rounded-full overflow-hidden mb-5 border-4 border-stone-100">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{member.name}</h3>
                <p className="text-amber-600 text-sm font-semibold mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{member.role}</p>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Accreditations &amp; Certifications</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert) => (
              <div key={cert.name} className="flex flex-col items-center gap-3 text-center py-6 border border-white/10 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${cert.icon} text-amber-400 text-2xl`}></i>
                </div>
                <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{cert.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
